import {
  MediaDetailResponseDto,
  ResolvedSuggestion,
} from "../common/ObjectTypes";
import {
  AcceptSelectedCardsRequest,
  AcceptSuggestionRequest,
  AssignSpeciesCardRequest,
  AssignSpeciesRequest,
  EditSuggestionRequest,
  ObservationCreateRequest,
  ObservationUpdateRequest,
  PredictionAppliedAt,
  RejectSelectedCardsRequest,
  RejectSuggestionRequest,
} from "../common/QueryTypes";

/**
 * Generate a fresh idempotency key for a commit action. Uses
 * `crypto.randomUUID()` when available, falling back to a timestamp+random
 * token for older/non-secure contexts. Kept pure (no axios import) so it and
 * the request builders below can be unit-tested without hitting the axios ESM
 * limitation (see CLAUDE.md test notes).
 * @returns a unique idempotency key string
 */
export const newIdempotencyKey = (): string => {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/** New field values captured by the observation editor for an edit action. */
export type EditFields = {
  taxonId: number;
  individualCount: number | null;
  comments: string | null;
};

/**
 * Build an accept request (§5.1.1 aggregate / §5.2.3 object). `predictionIds` is
 * the cluster's underlying predictions (exactly one for an object action).
 * `taxonId` is the resolved target taxon (required by the API). `parentTaxonId`
 * (object actions) lets the API decrement the parent card's aggregate
 * observation. Optional fields are omitted rather than sent null.
 * @returns the accept request body
 */
export const buildAcceptRequest = (
  mediaId: number,
  predictionIds: number[],
  taxonId: number,
  appliedAt: PredictionAppliedAt,
  idempotencyKey: string,
  parentTaxonId?: number | null,
): AcceptSuggestionRequest => ({
  media_id: mediaId,
  prediction_ids: predictionIds,
  taxon_id: taxonId,
  applied_at: appliedAt,
  ...(parentTaxonId != null ? { parent_taxon_id: parentTaxonId } : {}),
  ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
});

/**
 * Build a reject request (§5.1.2 aggregate / §5.2.1 object). `parentTaxonId` is
 * the card's own taxon (aggregate re-action) or the parent card's taxon (object).
 * @returns the reject request body
 */
export const buildRejectRequest = (
  mediaId: number,
  predictionIds: number[],
  appliedAt: PredictionAppliedAt,
  idempotencyKey: string,
  parentTaxonId?: number | null,
): RejectSuggestionRequest => ({
  media_id: mediaId,
  prediction_ids: predictionIds,
  applied_at: appliedAt,
  ...(parentTaxonId != null ? { parent_taxon_id: parentTaxonId } : {}),
  ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
});

/**
 * Build an edit request (§5.1.3 aggregate / §5.2.2 object). `new_taxon_id` is
 * sent only when the taxon changed; `new_count` only for an aggregate edit whose
 * count changed vs `originalCount` (the count the editor opened with — the card's
 * displayed count — not the raw cluster size); object edits are single-object, so
 * count is never sent; `comments` is always sent so a comments-only edit is possible.
 *
 * AKL-717: `originalTaxonId` is null for an object edit of an `unresolved` box —
 * a detection whose only label the project excluded, so there was no species to
 * start from. The API accepts a null original for object scope only, and the
 * chosen taxon then always differs from it, so `new_taxon_id` is always sent.
 * @returns the edit request body
 */
export const buildEditRequest = (
  mediaId: number,
  predictionIds: number[],
  originalTaxonId: number | null,
  appliedAt: PredictionAppliedAt,
  fields: EditFields,
  originalCount: number | null,
  idempotencyKey: string,
  parentTaxonId?: number | null,
): EditSuggestionRequest => {
  const newTaxonId =
    fields.taxonId !== originalTaxonId ? fields.taxonId : undefined;
  const newCount =
    appliedAt === "aggregate" &&
    fields.individualCount != null &&
    fields.individualCount !== originalCount
      ? fields.individualCount
      : undefined;
  return {
    media_id: mediaId,
    prediction_ids: predictionIds,
    original_taxon_id: originalTaxonId,
    ...(newTaxonId != null ? { new_taxon_id: newTaxonId } : {}),
    ...(newCount != null ? { new_count: newCount } : {}),
    comments: fields.comments,
    applied_at: appliedAt,
    ...(parentTaxonId != null ? { parent_taxon_id: parentTaxonId } : {}),
    ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
  };
};

/**
 * Build the create-observation request body for a manual observation.
 * @param mediaId the media asset id
 * @param fields the taxon/count/comments from the editor
 * @param idempotencyKey retry-safety key (omitted when empty)
 * @returns the request body
 */
export const buildObservationCreateRequest = (
  mediaId: number,
  fields: EditFields,
  idempotencyKey?: string,
): ObservationCreateRequest => ({
  media_id: mediaId,
  taxon_id: fields.taxonId,
  individual_count: fields.individualCount,
  comments: fields.comments,
  ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
});

/**
 * Build the assign-species request: several ticked cards, one chosen taxon, one
 * resulting observation (AKL-774).
 *
 * The card list goes across untouched - each entry keeps its own
 * `original_taxon_id`, which is how the server records `accepted` for the cards
 * already at the chosen taxon and `edited` for the ones it moved, behind a
 * single observation.
 *
 * `individual_count` and `comments` are omitted rather than sent null when the
 * editor has nothing for them, matching the other builders here. Omitting the
 * count makes the server default it to the number of boxes - the same number
 * the editor prefills - so the two cannot disagree.
 * @param mediaId the media asset id
 * @param cards the ticked cards, in panel order
 * @param fields the taxon/count/comments from the editor
 * @param idempotencyKey retry-safety key (omitted when empty)
 * @returns the request body
 */
export const buildAssignSpeciesRequest = (
  mediaId: number,
  cards: AssignSpeciesCardRequest[],
  fields: EditFields,
  idempotencyKey?: string,
): AssignSpeciesRequest => ({
  media_id: mediaId,
  cards,
  taxon_id: fields.taxonId,
  ...(fields.individualCount != null
    ? { individual_count: fields.individualCount }
    : {}),
  ...(fields.comments != null ? { comments: fields.comments } : {}),
  ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
});

/**
 * Build an accept-selected request (AKL-779): commit every ticked card at its
 * own taxon.
 *
 * There is no top-level `taxon_id` or `individual_count` — unlike
 * `buildAssignSpeciesRequest`, which carries both for the single observation it
 * creates. Here each card's own `original_taxon_id` is the taxon it commits at,
 * and each resulting observation counts its own boxes.
 * @param mediaId the media asset id
 * @param cards the ticked cards, in panel order
 * @param idempotencyKey retry-safety key; omitted from the body when blank
 * @returns the request body
 */
export const buildAcceptSelectedRequest = (
  mediaId: number,
  cards: AssignSpeciesCardRequest[],
  idempotencyKey?: string,
): AcceptSelectedCardsRequest => ({
  media_id: mediaId,
  cards,
  ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
});

/**
 * Build a reject-selected request (AKL-779): reject every ticked cluster.
 * @param mediaId the media asset id
 * @param cards the ticked cards, in panel order
 * @param idempotencyKey retry-safety key; omitted from the body when blank
 * @returns the request body
 */
export const buildRejectSelectedRequest = (
  mediaId: number,
  cards: AssignSpeciesCardRequest[],
  idempotencyKey?: string,
): RejectSelectedCardsRequest => ({
  media_id: mediaId,
  cards,
  ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
});

/**
 * Build the patch body for editing an existing observation. Sends the editor's
 * current taxon/count/comments (the editor always carries the full set, so this
 * is a full-field patch of the values the reviewer confirmed).
 * @param fields the taxon/count/comments from the editor
 * @returns the request body
 */
export const buildObservationUpdateRequest = (
  fields: EditFields,
): ObservationUpdateRequest => ({
  taxon_id: fields.taxonId,
  individual_count: fields.individualCount,
  comments: fields.comments,
});

/**
 * The parent aggregate card's resolved taxon for a box, if it belongs to a
 * suggestion cluster — used as `parent_taxon_id` on per-object actions so the API
 * can decrement that card's observation.
 *
 * Takes every prediction id the box could be clustered under, because the two
 * differ for a crop-classified object: the detection owns the bounding box while
 * the aggregator clusters on its object-level classification child (the
 * label-bearing prediction), so `underlying_prediction_ids` holds the
 * classification id. Matching on the detection id alone silently missed the
 * parent, and a box splitting out of that cluster then never decremented it.
 * @param detail the current media detail
 * @param predictionIds the box's candidate prediction ids (owning detection and
 *   label-bearing prediction; duplicates and equal values are harmless)
 * @returns the parent card's target taxon id, or undefined
 */
export const findParentTaxonId = (
  detail: MediaDetailResponseDto | null,
  ...predictionIds: number[]
): number | undefined =>
  // AKL-717: an `unresolved` entry has no taxon and is not a card, so it can
  // never be the parent aggregate a box leaves — skip it by construction.
  detail?.suggestions.find(
    (s): s is ResolvedSuggestion =>
      s.visibility !== "unresolved" &&
      predictionIds.some((id) => s.underlying_prediction_ids.includes(id)),
  )?.target_taxon_id;

/**
 * The comment currently stored on the observation a suggestion card / bounding
 * box owns, so the editor prefills it on a re-edit instead of opening blank —
 * which (because the editor always sends `comments`) would otherwise post a null
 * and silently wipe an existing note on a taxon/count-only edit. Matches the API's
 * reconciliation keys: an aggregate card owns the non-deleted media-level AI
 * observation for its taxon; a box owns the non-deleted object observation for its
 * source prediction.
 * @param detail the current media detail
 * @param match the aggregate taxon or the object's source prediction id
 * @returns the existing comment, or null when the card/box has no observation yet
 */
export const findExistingComment = (
  detail: MediaDetailResponseDto | null,
  match: { mediaTaxonId: number } | { sourcePredictionId: number },
): string | null => {
  const obs = detail?.observations.find((o) => {
    if (o.status === "deleted") return false;
    if ("mediaTaxonId" in match) {
      return (
        o.applies_to === "media" &&
        (o.source === "ai_approved" || o.source === "ai_edited") &&
        o.taxon_id === match.mediaTaxonId
      );
    }
    return (
      o.applies_to === "object" &&
      o.source_prediction_id === match.sourcePredictionId
    );
  });
  return obs?.comments ?? null;
};

/**
 * Mark-as-Blank pre-flight predicate (§5.3.3): a confirmation is required when
 * the media already has any non-blank observation OR any AI prediction without a
 * current review (an unactioned prediction). Suppressed predictions (overlay
 * excluded) are still real predictions and count as unactioned until reviewed.
 * @param detail the current media detail
 * @returns the two conditions and whether a confirmation is needed
 */
export const markBlankPreflight = (
  detail: MediaDetailResponseDto | null,
): {
  hasNonBlankObservations: boolean;
  hasUnactionedPredictions: boolean;
  needsConfirm: boolean;
} => {
  if (!detail) {
    return {
      hasNonBlankObservations: false,
      hasUnactionedPredictions: false,
      needsConfirm: false,
    };
  }
  const hasNonBlankObservations = detail.observations.some(
    (o) => o.status !== "deleted" && o.observation_type !== "blank",
  );
  const hasUnactionedPredictions = detail.predictions.some(
    (p) => p.latest_review == null,
  );
  return {
    hasNonBlankObservations,
    hasUnactionedPredictions,
    needsConfirm: hasNonBlankObservations || hasUnactionedPredictions,
  };
};

/** Which control opened the current confirmation (or, for mark-blank, requested it). */
export type MarkBlankTrigger = "save" | "save-next" | "direct";

/**
 * Whether pressing `key` should confirm the currently-open dialog as if its
 * confirm button had been clicked. Scoped to the zero-observation dialog only
 * (AKL-708) — a stray `B` must not confirm the `delete-obs` dialog.
 * @param kind the open `ConfirmState`'s kind, or undefined when none is open
 * @param key the pressed `KeyboardEvent.key`
 * @returns true when the keypress should trigger the dialog's confirm action
 */
export const isMarkBlankConfirmHotkey = (
  kind: "delete-obs" | "mark-blank" | "zero-obs" | undefined,
  key: string,
): boolean => kind === "zero-obs" && (key === "b" || key === "B");

/**
 * Whether a rejected commit is the zero-observation gate (§5.4/Q10) — the one
 * 422 the surface answers with the Mark-as-Blank-or-Cancel prompt rather than
 * an error toast.
 *
 * Matches on the structured `error_code`, not on the 422 status alone. `/save`
 * only ever raises 422 for this gate, but `/review/suggestions/accept-all`
 * (AKL-752) shares the same guard *and* can 422 for an unknown or
 * non-observable taxon — showing Mark as Blank for one of those would hide the
 * real error behind a prompt that makes no sense.
 * @param err the rejection from the axios wrapper (normalized `{status, data}`)
 * @returns true when the zero-observation prompt is the right response
 */
export const isZeroObservationsError = (err: unknown): boolean => {
  const e = err as {
    status?: number;
    data?: { detail?: { error_code?: unknown } };
  };
  return (
    e?.status === 422 && e?.data?.detail?.error_code === "zero_observations"
  );
};

/**
 * Whether a surface shortcut may still fire while a commit is in flight
 * (AKL-752 R4).
 *
 * Only the navigation keys: the arrows step between photos and Escape closes
 * the surface (or first clears a box / card selection, both purely local). With
 * commits now atomic server-side, navigating mid-flight cannot corrupt
 * anything — the transaction either landed or it did not, and the refetch on
 * return shows the truth.
 *
 * Everything that *commits* (`a`, `r`, `e`, `s`, `b`, `n`, Enter) stays blocked
 * while busy, so a second action cannot be fired on top of an in-flight one.
 * The zoom / box-toggle / help keys stay blocked too — harmless in principle,
 * but R4 asks for close and advance, and widening further is scope this did not
 * ask for.
 * @param key the pressed `KeyboardEvent.key`
 * @returns true when the keypress should still be handled during a commit
 */
export const isAllowedWhileCommitting = (key: string): boolean =>
  key === "ArrowLeft" || key === "ArrowRight" || key === "Escape";

/**
 * Whether a successful Mark as Blank should advance to the next photo.
 * `"direct"` (the Actions-panel button) and `"save-next"` (the zero-obs
 * dialog opened by Save & Next) advance when a next photo exists; `"save"`
 * (the zero-obs dialog opened by plain Save) never advances (§5.4.1).
 * @param triggeredBy which control led to this Mark as Blank
 * @param canStepNext whether a next photo exists in the current feed
 * @returns true when the reviewer should advance after the commit succeeds
 */
export const shouldAdvanceAfterMarkBlank = (
  triggeredBy: MarkBlankTrigger,
  canStepNext: boolean,
): boolean => canStepNext && triggeredBy !== "save";

export type SpeciesOption = {
  value: number;
  label: string;
  scientific: string;
};

/**
 * Whether the species picker should fall back to the full WI taxonomy search:
 * true when the project's effective species list is empty (§4.6).
 * @param effectiveListCount the number of items in the effective species list
 * @returns true when the WI-taxonomy fallback should be used
 */
export const shouldUseWiFallback = (effectiveListCount: number): boolean =>
  effectiveListCount === 0;
